
import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PRODUCTS, CATEGORY_MAP, getColorName, COLOR_SLUGS } from '../constants';
import { CartItem } from '../types';

const SIZE_GUIDE_IMAGES: Record<string, string> = {
  'Hoodies': '/talla_sudadera_capucha.png',
  'T-Shirts': '/talla_camiseta.png',
  'Sudaderas sin Capucha': '/talla_sudadera.png',
  'Default': 'https://images.unsplash.com/photo-1588850561407-ed78c282e89b?q=80&w=1000&auto=format&fit=crop'
};


/**
 * CATEGORÍAS QUE NO MOSTRARÁN EL BOTÓN DE GUÍA DE TALLAS
 * Si creas categorías nuevas como 'Posters' o 'Stickers', añádalas aquí.
 */
const NO_SIZE_GUIDE_CATEGORIES = ['Botellas', 'Tazas', 'Tote bag'];

interface ProductDetailProps {
  onAddToCart: (item: CartItem) => void;
}

const ProductDetail: React.FC<ProductDetailProps> = ({ onAddToCart }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const product = useMemo(() => PRODUCTS.find(p => p.id === id), [id]);

  const allImages = useMemo(() => {
    if (!product) return [];
    const images = [product.image, ...(product.gallery || [])];
    return Array.from(new Set(images));  // Mantiene todas las imágenes únicas para el carrusel
  }, [product]);

  const [mainImage, setMainImage] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  // ?color=000000 en la URL (enlaces de Google Shopping) preselecciona ese color
  const [searchParams] = useSearchParams();
  const colorParam = searchParams.get('color');
  const [selectedColor, setSelectedColor] = useState(
    product?.colors.find(c => colorParam && c.toLowerCase() === `#${colorParam.toLowerCase()}`) || product?.colors[0] || ''
  );
  const [quantity, setQuantity] = useState(1);

  // Estados para nuevas funcionalidades
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [showAddedModal, setShowAddedModal] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [isReviewSubmitted, setIsReviewSubmitted] = useState(false);

// Función para encontrar la imagen principal del color seleccionado
  const findImageForColor = (colorHex: string, images: string[]): string => {
    const colorName = getColorName(colorHex);
    const slug = COLOR_SLUGS[colorName] || colorName.toLowerCase().replace(/ /g, '');  // Fallback si no hay slug
    const lowerSlug = slug.toLowerCase();
    // Busca la primera imagen que contenga el slug (e.g., '_b_' para Blanco, 'negro' para Negro en tazas)
    return images.find(img => img.toLowerCase().includes(lowerSlug)) || images[0];  // Fallback a la primera si no match
  };

  // Actualiza mainImage cuando cambie el color seleccionado
  useEffect(() => {
    if (allImages.length > 0) {
      const newMain = findImageForColor(selectedColor, allImages);
      setMainImage(newMain);
    }
  }, [selectedColor, allImages]);

  const isExcludedCategory = product ? NO_SIZE_GUIDE_CATEGORIES.includes(product.category) : false;

  const getSizeGuideImage = () => {
    if (!product) return SIZE_GUIDE_IMAGES.Default;
    return SIZE_GUIDE_IMAGES[product.category] || SIZE_GUIDE_IMAGES.Default;
  };

  if (!product) return <div className="p-20 text-center font-bold italic uppercase tracking-tighter text-2xl">Producto no encontrado en el Paddock.</div>;

  const handleAddToCart = () => {
    // Solo validamos talla si la categoría NO está excluida
    if (!isExcludedCategory && product.sizes.length > 0 && !selectedSize) {
      alert("Por favor, selecciona una talla.");
      return;
    }
    
    onAddToCart({
      product,
      quantity,
      // Si está excluida, mandamos la primera talla disponible (ej. 'Talla Única') o un fallback
      size: isExcludedCategory ? (product.sizes[0] || 'Talla Única') : selectedSize,
      color: selectedColor || 'Original'
    });
    setShowAddedModal(true);
  };

  const handleReviewSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userRating === 0) {
      alert("Por favor, selecciona una puntuación.");
      return;
    }
    setIsReviewSubmitted(true);
    setReviewText('');
    setUserRating(0);
  };

  // Función para determinar si el check debe ser blanco o negro basado en el color de fondo
  const getCheckColor = (hex: string) => {
    const color = hex.toLowerCase();
    if (color === '#ffffff' || color === 'white' || color === '#f6f6f8') return 'text-black';
    return 'text-white';
  };

  return (
    <div className="layout-container flex h-full grow flex-col max-w-[1440px] mx-auto w-full px-4 lg:px-20 py-10">
      {/* Breadcrumbs */}
      <div className="flex flex-wrap gap-2 py-4 items-center mb-6">
        <Link to="/" className="text-gray-400 text-[10px] font-black uppercase tracking-widest hover:text-black transition-colors italic">Inicio</Link>
        <span className="material-symbols-outlined text-gray-300 text-xs">chevron_right</span>
        <Link to="/shop" className="text-gray-400 text-[10px] font-black uppercase tracking-widest hover:text-black transition-colors italic">Tienda</Link>
        <span className="material-symbols-outlined text-gray-300 text-xs">chevron_right</span>
        <span className="text-black text-[10px] font-black uppercase tracking-widest italic">{CATEGORY_MAP[product.category] || product.category}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
        {/* LADO IZQUIERDO: GALERÍA */}
        <div className="lg:col-span-7 flex flex-col md:flex-row gap-4">
          {/* Desktop Sidebar Gallery */}
          <div className="hidden md:flex flex-col gap-3 w-20 shrink-0">
            {allImages.map((img, idx) => (
              <button 
                key={idx}
                onClick={() => setMainImage(img)}
                className={`aspect-[4/5] w-full rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                  mainImage === img ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img src={img} className="w-full h-full object-cover" alt={`view ${idx}`} />
              </button>
            ))}
          </div>

          <div className="flex-1 flex flex-col gap-4">
            {/* Main Image Container */}
            <div className="aspect-[4/5] w-full rounded-2xl overflow-hidden bg-gray-50 shadow-sm relative group cursor-crosshair">
              <img 
                src={mainImage} 
                alt={product.name} 
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" 
              />
              {product.isNew && (
                <div className="absolute top-6 left-6 bg-black text-white text-[10px] font-black px-3 py-1.5 rounded uppercase tracking-[0.2em] italic">Nuevo Lanzamiento</div>
              )}
            </div>

            {/* Mobile Thumbnails (Horizontal Scroll) */}
            <div className="flex md:hidden gap-3 overflow-x-auto no-scrollbar pb-2">
              {allImages.map((img, idx) => (
                <button 
                  key={idx}
                  onClick={() => setMainImage(img)}
                  className={`aspect-[4/5] w-16 shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                    mainImage === img ? 'border-primary shadow-lg shadow-primary/20' : 'border-gray-100 opacity-60'
                  }`}
                >
                  <img src={img} className="w-full h-full object-cover" alt={`thumb ${idx}`} />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* LADO DERECHO: INFO */}
        <div className="lg:col-span-5 relative">
          <div className="sticky top-24 flex flex-col gap-8">
            <div className="border-b border-gray-100 pb-8">
              <div className="flex items-center justify-between mb-4">
                 <span className="text-primary font-bold uppercase tracking-[0.4em] text-[10px] italic">Colección {product.collection}</span>
              </div>
              <h1 className="text-black text-4xl md:text-5xl font-black tracking-tighter leading-none uppercase italic mb-4">{product.name}</h1>
              <p className="text-gray-400 text-lg font-medium italic mb-6">{product.description}</p>
              <div className="text-black text-4xl font-black tracking-tighter flex items-baseline gap-2 italic">
                {product.price.toFixed(2)}€
                <span className="text-xs text-gray-400 font-bold uppercase tracking-widest ml-2">IVA Incluido</span>
              </div>
            </div>

            {/* Descripción Extendida */}
            <div className="text-gray-500 text-sm md:text-base leading-relaxed font-medium italic">
              {product.details}
            </div>

            {/* Selector de Color */}
            {product.colors.length > 0 && (
              <div className="flex flex-col gap-4">
                <span className="text-black text-[10px] font-black uppercase tracking-[0.3em] italic">Color</span>
                <div className="flex gap-4">
                  {product.colors.map(color => (
                    <button 
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`size-10 rounded-full border-4 transition-all transform hover:scale-110 flex items-center justify-center ${
                        selectedColor === color ? 'border-primary scale-110 shadow-lg' : 'border-gray-100'
                      }`}
                      style={{ backgroundColor: color }}
                    >
                      {selectedColor === color && <span className={`material-symbols-outlined text-xs font-black ${getCheckColor(color)}`}>check</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Selector de Talla */}
            {!isExcludedCategory && product.sizes.length > 0 && (
              <div className="flex flex-col gap-4">
                <div className="flex justify-between items-center">
                  <span className="text-black text-[10px] font-black uppercase tracking-[0.3em] italic">Talla</span>
                  {/* Lógica dinámica: solo mostramos si la categoría NO está en la lista de exclusión */}
                  {!NO_SIZE_GUIDE_CATEGORIES.includes(product.category) && (
                    <button 
                      onClick={() => setIsSizeGuideOpen(true)}
                      className="text-primary text-[9px] font-black uppercase tracking-widest hover:underline italic flex items-center gap-2 group"
                    >
                      <span className="material-symbols-outlined text-xs transition-transform group-hover:rotate-12">straighten</span>
                      Guía de Tallas
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-5 gap-3">
                  {product.sizes.map(size => (
                    <button 
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`h-12 flex items-center justify-center rounded-xl transition-all font-black text-xs italic ${
                        selectedSize === size 
                          ? 'bg-primary text-white shadow-xl shadow-primary/20 scale-105' 
                          : 'bg-white border border-gray-100 text-gray-400 hover:border-black hover:text-black'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-4 pt-4">
              <button 
                onClick={handleAddToCart}
                className="group flex w-full cursor-pointer items-center justify-center rounded-2xl h-16 md:h-20 bg-black text-white text-base font-black uppercase tracking-[0.2em] hover:bg-neutral-800 transition-all shadow-2xl active:scale-95 italic"
              >
                Añadir
                <span className="material-symbols-outlined ml-3 transition-transform group-hover:translate-x-1">shopping_cart</span>
              </button>
{/* Banner discreto de envío gratis - justo debajo */}
  <p className="text-center text-gray-500 text-sm font-medium italic tracking-wide mt-2">
    Envío GRATIS en pedidos a partir de 65€
  </p>
            </div>

            {/* Sistema de Reseñas */}
            <div className="mt-10 border-t border-gray-100 pt-10">
              <h3 className="text-black text-[10px] font-black uppercase tracking-[0.3em] mb-6 italic">Muro de Boxes (Opiniones)</h3>
              {isReviewSubmitted ? (
                <div className="bg-green-50 p-6 rounded-2xl border border-green-100 text-center animate-fade-in">
                  <span className="material-symbols-outlined text-green-600 text-3xl mb-2">send</span>
                  <p className="text-green-800 font-bold text-sm uppercase tracking-tight italic">¡opinión Recibida!</p>
                  <p className="text-green-600 text-[10px] mt-1 uppercase font-medium">Tu reseña será analizada por el equipo técnico y publicada pronto.</p>
                </div>
              ) : (
                <form onSubmit={handleReviewSubmit} className="space-y-4">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setUserRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="p-1 focus:outline-none transition-transform active:scale-125"
                      >
                        <span className={`material-symbols-outlined text-2xl ${
                          (hoverRating || userRating) >= star ? 'fill-current text-yellow-400' : 'text-gray-200'
                        }`}>star</span>
                      </button>
                    ))}
                  </div>
                  <textarea
                    required
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder="Escribe tu opinión sobre el producto..."
                    className="w-full bg-gray-50 border border-transparent focus:border-gray-200 rounded-xl p-4 text-sm outline-none resize-none h-24 transition-all italic"
                  />
                  <button type="submit" className="text-[10px] font-black uppercase tracking-widest text-primary hover:underline italic">Enviar opinión</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL GUÍA DE TALLAS - COMPACTO */}
      {isSizeGuideOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fade-in" onClick={() => setIsSizeGuideOpen(false)}>
          <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl relative animate-scale-up" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setIsSizeGuideOpen(false)} 
              className="absolute top-4 right-4 z-20 size-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-primary transition-colors shadow-lg active:scale-90"
            >
              <span className="material-symbols-outlined text-xl">close</span>
            </button>
            <div className="p-8">
              <div className="mb-6">
                <span className="text-primary font-bold uppercase tracking-[0.4em] text-[8px] italic">Technical Specs</span>
                <h3 className="text-xl font-black uppercase italic tracking-tighter mt-1">Guía: {CATEGORY_MAP[product.category] || product.category}</h3>
              </div>
              <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100 flex items-center justify-center min-h-[300px]">
                <img 
                  src={getSizeGuideImage()} 
                  alt={`Size Guide ${product.category}`} 
                  className="max-w-full h-auto object-contain" 
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CONFIRMACIÓN CARRITO */}
      {showAddedModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-fade-in">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 md:p-10 shadow-2xl text-center animate-scale-up border border-gray-100">
            <div className="size-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <span className="material-symbols-outlined text-primary text-4xl font-black">shopping_cart_checkout</span>
            </div>
            <h3 className="text-2xl font-black uppercase italic tracking-tighter mb-2">¡Unidad en el carrito!</h3>
            <p className="text-gray-500 text-sm mb-10 italic">{product.name} ha sido añadido a tu carrito. ¿Cuál es tu próximo movimiento?</p>
            
            <div className="flex flex-col gap-4">
              <button 
                onClick={() => navigate('/checkout')}
                className="bg-black text-white h-16 rounded-2xl font-black uppercase tracking-widest italic hover:bg-neutral-800 transition-all shadow-xl active:scale-95"
              >
                Ir a Finalizar Pedido
              </button>
              <button 
                onClick={() => setShowAddedModal(false)}
                className="text-gray-400 h-16 font-black uppercase tracking-widest text-[10px] hover:text-black transition-colors italic"
              >
                Seguir Explorando Tienda
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
